import { lazy, Suspense } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { trackEvent } from '../../analytics'

// Lazy-loaded so three.js ships as a separate chunk (keeps the main bundle small).
// CSS gradients + orbs below act as the fallback until the 3D canvas mounts.
const HeroBackground = lazy(() =>
  import('../../components/three/HeroBackground').then((m) => ({ default: m.HeroBackground }))
)

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
}

const item = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
}

export function HeroSection() {
  return (
    <section className="relative min-h-[100dvh] flex items-center justify-center px-4 overflow-hidden">
      <Suspense fallback={null}>
        <HeroBackground />
      </Suspense>
      <div className="absolute inset-0 bg-white/70 dark:bg-[#0F172A]/80" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-white dark:via-[#0F172A]/50 dark:to-[#0F172A]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(26,69,176,0.15),transparent)] dark:bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(255,215,0,0.15),transparent)]" />

      {/* Floating orbs - light theme only */}
      <div className="absolute top-1/4 left-1/4 w-72 h-72 light-orb light-orb-primary animate-float opacity-50 dark:opacity-0 transition-opacity duration-500" aria-hidden />
      <div className="absolute bottom-1/3 right-1/4 w-96 h-96 light-orb light-orb-accent animate-float opacity-40 dark:opacity-0 transition-opacity duration-500" style={{ animationDelay: '1s' }} aria-hidden />

      <motion.div
        className="relative w-full max-w-4xl mx-auto text-center py-8"
        variants={container}
        initial="hidden"
        animate="visible"
      >
        <motion.h1
          className="text-hero font-bold font-display tracking-tight text-primary dark:text-white"
          variants={item}
          transition={{ duration: 0.5 }}
        >
          Systems That Turn Attention Into Revenue
        </motion.h1>
        <motion.p
          className="mt-3 sm:mt-4 text-hero-sm text-slate-600 dark:text-slate-300 max-w-2xl mx-auto"
          variants={item}
          transition={{ duration: 0.5 }}
        >
          We help high-value businesses grow through structured brand presence, predictable lead generation, and effective sales systems.
        </motion.p>

        {/* Compact "Our focus is simple" card */}
        <motion.div
          className="mt-5 sm:mt-6 max-w-2xl mx-auto rounded-2xl glass border px-4 py-3.5 sm:px-5 sm:py-4 text-center border-primary/20 dark:border-white/10 bg-primary/[0.06] dark:bg-transparent shadow-[0_4px_24px_-4px_rgba(26,69,176,0.18)] dark:shadow-none"
          variants={item}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs font-semibold uppercase tracking-wider text-primary dark:text-accent mb-2 sm:mb-2.5">
            Our focus is simple
          </p>
          <p className="text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-snug">
            Create marketing systems that consistently generate opportunities and help businesses convert them into real revenue. Not random campaigns. Not vanity metrics. Just clear strategy, measurable impact, and scalable growth.
          </p>
        </motion.div>

        <motion.div
          className="mt-6 sm:mt-8"
          variants={item}
          transition={{ duration: 0.5 }}
        >
          <Link to="/contact" className="btn-primary relative overflow-hidden group" onClick={() => trackEvent('cta_click', { cta_name: 'book_strategy_call', from: 'hero' })}>
            <span className="relative z-10">Book Strategy Call</span>
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" aria-hidden />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  )
}
